import {
  LINETYPE_PATTERNS,
  dashPolyline,
  dimensionGeometry,
  entityPaths,
  type Entity,
  type EntityId,
  type Layer,
  type Vec2,
} from '@lakaran/geometry';

export interface RenderPath {
  points: Vec2[];
  closed: boolean;
}

/** Renderer-facing geometry: everything is already in world millimetres. */
export interface RenderShape {
  entityId: EntityId;
  layerId: string;
  color: number;
  lineWidthPx: number;
  paths: RenderPath[];
  /** Closed polygons to fill (dimension arrowheads). */
  fills: RenderPath[];
}

export interface TessellateOptions {
  /** World-space chord tolerance, usually derived from zoom (e.g. 0.35px / scale). */
  chordTolerance: number;
}

export function buildRenderShape(
  entity: Entity,
  layer: Layer,
  options: TessellateOptions,
): RenderShape | null {
  if (entity.type === 'text') return null;

  const color = entity.color ?? layer.color;
  const lineWidthPx = entity.lineWidth ?? layer.lineWidth;
  const linetype = entity.linetype ?? layer.linetype;

  const rawPaths = entityPaths(entity, { chordTolerance: options.chordTolerance });
  if (rawPaths.length === 0) return null;

  const paths: RenderPath[] = [];
  const pattern = LINETYPE_PATTERNS[linetype];
  if (pattern.length === 0) {
    for (const path of rawPaths) paths.push({ points: path.points, closed: path.closed });
  } else {
    for (const path of rawPaths) {
      if (path.points.length < 2) continue;
      for (const run of dashPolyline(path.points, path.closed, pattern)) {
        paths.push({ points: run, closed: false });
      }
    }
  }

  const fills: RenderPath[] = [];
  if (entity.type === 'dimension') {
    for (const arrow of dimensionGeometry(entity).arrows) {
      fills.push({ points: arrow, closed: true });
    }
  }

  if (paths.length === 0 && fills.length === 0) return null;
  return { entityId: entity.id, layerId: entity.layerId, color, lineWidthPx, paths, fills };
}
